import { Injectable } from '@angular/core';

import { filter, map, merge, Observable, tap } from 'rxjs';

import { AccountUser } from '@shared/user/models';
import { WebSocketEventsEnum } from '@shared/websocket';
import { AuthUser } from '@shared/signup/models';
import { UserApiService } from '@tablesready/frontend-auth';
import { AuthAccountUserQuery, AuthAccountUserStore } from '../states';
import { SocketService } from './socket.service';

@Injectable({ providedIn: 'root' })
export class AuthAccountUserService {
  constructor(
    private readonly userApiService: UserApiService,
    private readonly query: AuthAccountUserQuery,
    private readonly store: AuthAccountUserStore,
    private readonly socketService: SocketService,
  ) {
  }

  load(): Observable<AccountUser[]> {
    return this.userApiService.getAccountUsers()
      .pipe(tap(accountUsers => this.store.set(accountUsers)));
  }

  selectAll(): Observable<AccountUser[]> {
    return this.query.selectAll();
  }

  getAll(): AccountUser[] {
    return this.query.getAll();
  }

  getById(id: string): AccountUser {
    return this.query.getEntity(id);
  }

  listenToChanges(user: AuthUser) {
    return merge(
      this.socketService.getEventStream<AccountUser>(WebSocketEventsEnum.accountUserCreated)
        .pipe(
          filter(accountUser => accountUser.userId === user._id),
          tap(accountUser => this.store.add(accountUser)),
        ),
      this.socketService.getEventStream<AccountUser[]>(WebSocketEventsEnum.accountUserUpdated)
        .pipe(
          map(accountUsers => accountUsers.filter(accountUser => accountUser.userId === user?._id)),
          tap(accountUsers => {
            for (let i = 0; i < accountUsers.length; i++) {
              this.store.update(accountUsers[i].id, accountUsers[i]);
            }
          }),
        ),
      this.socketService.getEventStream<string>(WebSocketEventsEnum.accountUserDeleted)
        .pipe(tap(id => this.store.remove(id))),
    );
  }

  reset() {
    return this.store.reset();
  }
}
